export default class Buttons {
	static pluginName = 'buttons';

	/**
	 * @constructor
	 * @param {Object} ctx - the carousel context that the buttons will control
	 */
	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;
		_.navOptions = {};
		_.prevButton = null;
		_.nextButton = null;
		_.smartButtonImage = null;
		_.smartButtonsRaf = 0;

		// define all event handlers in one object
		_.handlers = {
			// handler for prev button click
			previousClick: (e) => {
				// trigger user interacted event
				_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'button', event: e });
				_.ctx.commands.previous(5);
			},

			// handler for next button click
			nextClick: (e) => {
				// trigger user interacted event
				_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'button', event: e });
				_.ctx.commands.next(-5);
			},

			// handler for button focus
			buttonFocus: (e) => {
				// pause autoplay on focus if enabled
				_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'focus', event: e });
			},

			// handler for options changed event
			optionsChanged: () => {
				_.reInit();
			},

			// debounced check for multiple events
			debouncedCheckDisabledState: _.ctx.utils.debounce(() => {
				_.checkDisabledState();
			}, 4),

			// debounce resize so we only measure after a resize burst settles;
			// fast drags otherwise fire many measurements against a still-reflowing layout.
			windowResize: _.ctx.utils.debounce(() => {
				_.#scheduleSmartButtonsUpdate();
			}, 60),

			imageLoad: () => {
				_.#scheduleSmartButtonsUpdate();
			},
		};

		_.init();
	}

	/**
	 * initialize the navigation buttons
	 */
	init() {
		const _ = this;
		_.navOptions = _.ctx.store.getOptions().navigation || {};

		// pull buttons from DOM
		_.queryDOM();
		// build them if they don't exist
		// must be in this order for in the DOM
		if (!_.nextButton) _.nextButton = _.buildButton('next');
		if (!_.prevButton) _.prevButton = _.buildButton('prev');

		// bind events and set up UI
		_.checkButtonOptions();
		_.bindEvents();
		_.checkDisabledState();

		setTimeout(() => {
			_.updateSmartButtons();
		}, 8);

		setTimeout(() => {
			_.updateSmartButtons();
		}, 30);
	}

	/**
	 * reinitialize on carousel option changes
	 */
	reInit() {
		const _ = this;
		_.navOptions = _.ctx.store.getOptions().navigation || {};
		_.checkButtonOptions();
		_.checkDisabledState();
		_.#scheduleSmartButtonsUpdate();
	}

	/**
	 * bind all events (DOM and emitter)
	 */
	bindEvents() {
		const _ = this;

		// DOM events for buttons
		if (_.prevButton) {
			_.prevButton.addEventListener('click', _.handlers.previousClick, true);
			_.prevButton.addEventListener('focus', _.handlers.buttonFocus);
		}
		if (_.nextButton) {
			_.nextButton.addEventListener('click', _.handlers.nextClick, true);
			_.nextButton.addEventListener('focus', _.handlers.buttonFocus);
		}

		// bind to first image load for smart buttons
		const slides = _.ctx.store.getSlides();
		if (slides.length > 0) {
			let firstImage = slides[0]?.querySelector('img') || false;
			if (firstImage && firstImage.complete) {
				_.#scheduleSmartButtonsUpdate();
			} else if (firstImage) {
				_.smartButtonImage = firstImage;
				firstImage.addEventListener('load', _.handlers.imageLoad, { once: true });
			}
		}

		// emitter events
		_.ctx.emitter.on(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
		_.ctx.emitter.on(_.ctx.events.store.pageIndexChanged, _.handlers.debouncedCheckDisabledState);
		_.ctx.emitter.on(_.ctx.events.store.pageCountChanged, _.handlers.debouncedCheckDisabledState);
		_.ctx.emitter.on(_.ctx.events.store.slidesChanged, _.handlers.debouncedCheckDisabledState);
		_.ctx.emitter.on(_.ctx.events.store.canLoopChanged, _.handlers.debouncedCheckDisabledState);
		_.ctx.emitter.on(_.ctx.events.window.resize, _.handlers.windowResize);
	}

	/**
	 * check button options and show/hide buttons accordingly
	 */
	checkButtonOptions() {
		this.navOptions.showButtons ? this.showButtons() : this.hideButtons();
	}

	/**
	 * query buttons from the dom
	 */
	queryDOM() {
		const _ = this;
		const navOptions = _.navOptions;

		// first check to see if the user gave us a selector
		if (navOptions.previousButtonSelector) {
			_.prevButton = document.querySelector(navOptions.previousButtonSelector);
		}

		if (navOptions.nextButtonSelector) {
			_.nextButton = document.querySelector(navOptions.nextButtonSelector);
		}

		// then check to see if there are any buttons inside the carousel
		if (!_.prevButton) {
			// query all child buttons
			const allPrevButtons = _.ctx.carousel.querySelectorAll('[data-action-tarot-previous]');
			// filter them so that their closest ancestor carousel is the current one.
			_.prevButton =
				Array.from(allPrevButtons).find(
					(btn) => btn.closest('tarot-carousel') === _.ctx.carousel
				) || null;
		}

		if (!_.nextButton) {
			const allNextButtons = _.ctx.carousel.querySelectorAll('[data-action-tarot-next]');
			_.nextButton =
				Array.from(allNextButtons).find(
					(btn) => btn.closest('tarot-carousel') === _.ctx.carousel
				) || null;
		}
	}

	/**
	 * build a navigation button if it doesn't exist
	 * @param {string} type - 'prev' or 'next'
	 * @returns {Element} the created button element
	 */
	buildButton(type) {
		const isNext = type === 'next';
		const dataAttr = isNext ? 'data-action-tarot-next' : 'data-action-tarot-previous';
		const ariaLabel = isNext ? 'next slide' : 'previous slide';
		const svgTitle = isNext ? 'angle right' : 'angle left';
		const svgPath = isNext ? 'm11 25 9-9-9-9' : 'm21 7-9 9 9 9';

		const html = `
			<button class="tarot-button" ${dataAttr} aria-label="${ariaLabel}">
				<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
					<title>${svgTitle}</title>
					<path d="${svgPath}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
				</svg>
			</button>
		`;
		this.ctx.carousel.insertAdjacentHTML('afterbegin', html);
		return this.ctx.carousel.querySelector(`[${dataAttr}]`);
	}

	/**
	 * show the navigation buttons
	 */
	showButtons() {
		const _ = this;
		const prevButton = _.prevButton;
		const nextButton = _.nextButton;

		// Even if showButtons: true they can set each button like
		// showPreviousButton: false to only show one
		if (prevButton && _.navOptions.showPreviousButton !== false) {
			prevButton.style.display = '';
			prevButton.setAttribute('aria-label', 'previous');
			prevButton.classList.add('tarot-button', 'tarot-previous');
		} else {
			_.hidePrevButton();
		}

		if (nextButton && _.navOptions.showNextButton !== false) {
			nextButton.style.display = '';
			nextButton.setAttribute('aria-label', 'next');
			nextButton.classList.add('tarot-button', 'tarot-next');
		} else {
			_.hideNextButton();
		}
	}

	/**
	 * hide both buttons
	 */
	hideButtons() {
		this.hidePrevButton();
		this.hideNextButton();
	}

	/**
	 * hide the prev button
	 */
	hidePrevButton() {
		if (this.prevButton) this.prevButton.style.display = 'none';
	}

	/**
	 * hide the next button
	 */
	hideNextButton() {
		if (this.nextButton) this.nextButton.style.display = 'none';
	}

	// defer updateSmartButtons by one rAF so the FrameEngine's rAF has a chance
	// to paint the new --tarot-slide-width before we measure the image height.
	// otherwise a fast resize reads a stale offsetHeight and the button lands high.
	#scheduleSmartButtonsUpdate() {
		const _ = this;
		if (_.smartButtonsRaf) cancelAnimationFrame(_.smartButtonsRaf);
		_.smartButtonsRaf = requestAnimationFrame(() => {
			_.smartButtonsRaf = 0;
			_.updateSmartButtons();
		});
	}

	// smart buttons
	updateSmartButtons() {
		const _ = this;
		const smartButtons = _.ctx.store.getOptions().navigation?.smartButtons || false;
		const prevButton = _.prevButton;
		const nextButton = _.nextButton;
		const viewport = _.ctx.viewport;
		const slides = _.ctx.store.getSlides();

		// bail if we don't have a viewport or any slides
		if (!viewport) return;
		if (!slides || slides.length === 0) return;

		// remove classes if not using smart buttons
		if (!smartButtons) {
			if (prevButton) {
				prevButton.classList.remove('tarot-smart-position');
			}
			if (nextButton) {
				nextButton.classList.remove('tarot-smart-position');
			}
			return;
		}

		// fetch info from the carousel
		const firstSlide = slides[0];
		if (!firstSlide) return;

		// read computed styles in case we need padding
		const viewportStyles = window.getComputedStyle(viewport);
		const viewportTopPadding = parseInt(viewportStyles.paddingTop) || 0;

		// figure out a rough button vertical position
		let buttonTopPos = viewport.offsetHeight;

		// if the first slide has an <img>, maybe use its height
		const firstImage = firstSlide.querySelector('img');
		if (firstImage && firstImage.offsetHeight < buttonTopPos) {
			// add top padding if relevant
			buttonTopPos = firstImage.height + viewportTopPadding * 2;
		}

		// half it so it's approximately centered
		buttonTopPos = buttonTopPos / 2;

		// set prev button (vertical only — user controls horizontal via CSS)
		if (prevButton) {
			prevButton.classList.add('tarot-smart-position');
			prevButton.style.top = `${buttonTopPos}px`;
		}

		// set next button (vertical only — user controls horizontal via CSS)
		if (nextButton) {
			nextButton.classList.add('tarot-smart-position');
			nextButton.style.top = `${buttonTopPos}px`;
		}
	}

	/**
	 * check and update disabled state of buttons
	 */
	checkDisabledState() {
		const _ = this;
		const state = _.ctx.store.getState();
		const page = state.pageIndex;
		const pageCount = state.pageCount;
		const slideCount = state.slideCount;

		// Edge case: no slides or only 1 page - disable both
		if (slideCount === 0 || pageCount <= 1) {
			if (_.prevButton) _.prevButton.disabled = true;
			if (_.nextButton) _.nextButton.disabled = true;
			return;
		}

		// Looping enabled - both buttons always enabled
		if (state.canLoop) {
			if (_.prevButton) _.prevButton.disabled = false;
			if (_.nextButton) _.nextButton.disabled = false;
			return;
		}

		// Not looping - disable at boundaries
		if (_.prevButton) _.prevButton.disabled = page === 0;
		if (_.nextButton) _.nextButton.disabled = page === pageCount - 1;
	}

	/**
	 * destroy the buttons, unbinding all events
	 */
	destroy() {
		const _ = this;
		const emitter = _.ctx.emitter;
		const handlers = _.handlers;
		const EVENTS = _.ctx.events;

		// unbind DOM events
		if (_.prevButton) {
			_.prevButton.removeEventListener('click', handlers.previousClick, true);
			_.prevButton.removeEventListener('focus', handlers.buttonFocus);
		}
		if (_.nextButton) {
			_.nextButton.removeEventListener('click', handlers.nextClick, true);
			_.nextButton.removeEventListener('focus', handlers.buttonFocus);
		}
		if (_.smartButtonImage) {
			_.smartButtonImage.removeEventListener('load', _.handlers.imageLoad);
			_.smartButtonImage = null;
		}

		// unbind emitter events
		emitter.off(EVENTS.store.optionsChanged, handlers.optionsChanged);
		emitter.off(EVENTS.store.pageIndexChanged, handlers.debouncedCheckDisabledState);
		emitter.off(EVENTS.store.pageCountChanged, handlers.debouncedCheckDisabledState);
		emitter.off(EVENTS.store.slidesChanged, handlers.debouncedCheckDisabledState);
		emitter.off(EVENTS.store.canLoopChanged, handlers.debouncedCheckDisabledState);
		emitter.off(EVENTS.window.resize, handlers.windowResize);

		// cancel debounced functions
		if (handlers.debouncedCheckDisabledState?.cancel) {
			handlers.debouncedCheckDisabledState.cancel();
		}
		if (handlers.windowResize?.cancel) {
			handlers.windowResize.cancel();
		}

		// cancel pending smart-buttons rAF
		if (_.smartButtonsRaf) {
			cancelAnimationFrame(_.smartButtonsRaf);
			_.smartButtonsRaf = 0;
		}

		_.prevButton = null;
		_.nextButton = null;
	}
}
